// ============================================
// FORMATTING / CURRENCY / TIMEZONE HELPERS
// Extracted from App.js (App.js modularization, Sep 2026) — pure functions,
// no state or component dependencies, so they were safe to lift unchanged.
// ============================================

// Currency formatting helpers
export const CURRENCY_SYMBOLS = {
  'USD': '$',
  'EUR': '€',
  'JPY': '¥',
  'GBP': '£',
  'AUD': 'A$',
  'CAD': 'C$',
  'CHF': 'CHF',
  'CNH': '¥',
  'HKD': 'HK$',
  'NZD': 'NZ$'
};

export const CURRENCY_NAMES = {
  'USD': 'US Dollar',
  'EUR': 'Euro',
  'JPY': 'Japanese Yen',
  'GBP': 'British Pound',
  'AUD': 'Australian Dollar',
  'CAD': 'Canadian Dollar',
  'CHF': 'Swiss Franc',
  'CNH': 'Chinese Renminbi',
  'HKD': 'Hong Kong Dollar',
  'NZD': 'New Zealand Dollar'
};

export const formatPrice = (amount, currency) => {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  if (currency === 'JPY' || currency === 'CNH') {
    return `${symbol}${Math.round(amount)}`;
  }
  return `${symbol}${amount.toFixed(2)}`;
};

export const getNextQuarterHour = () => {
  const now = new Date();
  const minutes = Math.ceil(now.getMinutes() / 15) * 15;
  now.setMinutes(minutes);
  now.setSeconds(0);
  const hours = String(now.getHours()).padStart(2, '0');
  const mins = String(now.getMinutes()).padStart(2, '0');
  return `${hours}:${mins}`;
};

export const getEndTime = (startTime) => {
  if (!startTime) return '';
  const [hours, minutes] = startTime.split(':');
  const date = new Date();
  date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
  date.setHours(date.getHours() + 1); // Add 1 hour
  const endHours = String(date.getHours()).padStart(2, '0');
  const endMins = String(date.getMinutes()).padStart(2, '0');
  return `${endHours}:${endMins}`;
};

export const formatTime = (timeStr) => {
  if (!timeStr) return '';
  // Remove seconds if present (HH:MM:SS → HH:MM)
  const parts = timeStr.split(':');
  return `${parts[0]}:${parts[1]}`;
};

// Short "d-mmm" style date for poll-closing copy (e.g. "2 Sep") — stays
// accurate without needing to be recalculated relative to "today" every
// render, unlike a "closes in N days" countdown.
export const formatShortDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDate();
  const month = d.toLocaleDateString('en-GB', { month: 'short' });
  return `${day} ${month}`;
};

// Date + time formatter for a real timestamp (e.g. a poll response's
// updated_at) — distinct from formatShortDate above, which is for
// date-only strings like closesOn and always reads noon.
export const formatShortDateTime = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const day = d.getDate();
  const month = d.toLocaleDateString('en-GB', { month: 'short' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${day} ${month} at ${time}`;
};

// Full event date respecting the account's preferred_date_format setting
// (same four formats used for the tier-error message dates) — defaults to
// "D-Mmm-YYYY" (e.g. "2-Sep-2026") when no preference is set.
export const formatDateByPref = (dateStr, prefFormat) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const dd = dt.getDate(), mm = dt.getMonth() + 1, yyyy = dt.getFullYear();
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (prefFormat === 'MM/DD/YYYY') return `${mm}/${dd}/${yyyy}`;
  if (prefFormat === 'DD/MM/YYYY') return `${dd}/${mm}/${yyyy}`;
  if (prefFormat === 'YYYY-MM-DD') return `${yyyy}-${String(mm).padStart(2,'0')}-${String(dd).padStart(2,'0')}`;
  return `${dd}-${names[mm-1]}-${yyyy}`;
};

// (24 Oct 2026, 8.40a, decision 100) An event's date as each person asked for it: their
// Date Format, with the day of the week in front unless they untick "Show the day of the
// week with event dates" (users.show_weekday, on by default): "Sunday 23-Aug-2026".
// For event dates in the app: event cards and the poll and results page headers. The
// hosting schedule ("Sun 23 Aug"), the History page and dates written in full in
// questions and messages ("Sunday 25 October", decision 26) keep their own formats.
// Read from YYYY-MM-DD as it is, so no time zone can shift the day.
export const formatEventDateByPref = (dateStr, prefFormat, showWeekday = true) => {
  const iso = String(dateStr || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return dateStr ? String(dateStr) : '';
  const date = formatDateByPref(iso, prefFormat);
  if (showWeekday === false) return date;
  const weekday = new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', timeZone: 'UTC' });
  return `${weekday} ${date}`;
};

// Return the timezone abbreviation (e.g. 'CEST', 'CET') for a given IANA tzid and date.
// Uses Intl.DateTimeFormat to resolve DST automatically — no manual lookup needed.
export const getTzAbbr = (tzid, dateStr) => {
  if (!tzid) return '';
  try {
    const date = dateStr ? new Date(dateStr + 'T12:00:00') : new Date();
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: tzid,
      timeZoneName: 'short'
    }).formatToParts(date);
    const part = parts.find(p => p.type === 'timeZoneName');
    // Some browsers return 'GMT+2' style — convert to a friendlier label via fallback map
    if (part && part.value && !part.value.startsWith('GMT')) return part.value;
    return part?.value || '';
  } catch (_) {
    return '';
  }
};

// IANA timezone list for the timezone picker
export const TIMEZONES = [
  { value: 'Europe/London',     label: 'London (GMT/BST)' },
  { value: 'Europe/Paris',      label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin',     label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Madrid',     label: 'Madrid (CET/CEST)' },
  { value: 'Europe/Rome',       label: 'Rome (CET/CEST)' },
  { value: 'Europe/Amsterdam',  label: 'Amsterdam (CET/CEST)' },
  { value: 'Europe/Brussels',   label: 'Brussels (CET/CEST)' },
  { value: 'Europe/Zurich',     label: 'Zurich (CET/CEST)' },
  { value: 'Europe/Lisbon',     label: 'Lisbon (WET/WEST)' },
  { value: 'Europe/Athens',     label: 'Athens (EET/EEST)' },
  { value: 'Europe/Helsinki',   label: 'Helsinki (EET/EEST)' },
  { value: 'America/New_York',  label: 'New York (EST/EDT)' },
  { value: 'America/Chicago',   label: 'Chicago (CST/CDT)' },
  { value: 'America/Denver',    label: 'Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Toronto',   label: 'Toronto (EST/EDT)' },
  { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT/BRST)' },
  { value: 'Asia/Tokyo',        label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai',     label: 'Shanghai (CST)' },
  { value: 'Asia/Dubai',        label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata',      label: 'Kolkata (IST)' },
  { value: 'Asia/Singapore',    label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney',  label: 'Sydney (AEST/AEDT)' },
  { value: 'Pacific/Auckland',  label: 'Auckland (NZST/NZDT)' },
  { value: 'UTC',               label: 'UTC' },
];

// "Signed in as" display, standardized 5 Sep 2026 across every screen that
// shows one. Precedence: preferred name, else first+last name, else just
// the email.
// (21 Sep 2026) The email is no longer shown alongside a name: it was a testing aid, and a
// user's own address now sits at the top of Settings > Profile. It remains only as the
// fallback when there is no name at all, so the line is never blank.
//   formatSignedInAs('Neil (GA)', 'Neil', 'Miller', 'neil.miller@telematrixconsulting.com')
//     -> 'Neil (GA)'
//   formatSignedInAs('', 'Neil', 'Miller', 'neil.miller@telematrixconsulting.com')
//     -> 'Neil Miller'
//   formatSignedInAs('', '', '', 'neil.miller@telematrixconsulting.com')
//     -> 'neil.miller@telematrixconsulting.com'
// (2 Oct 2026) Optional 5th argument: the primary member's preferred name
// when the signed-in person is that member's secondary contact on the
// calendar being shown -> 'Allan of Turea & Allan'. Omitted everywhere else.
// (12 Oct 2026, decision 102) A secondary contact is shown by First Name only,
// never by a Preferred Name (since 8.28 they have none; Allan Spencer's was a
// leftover): 'Max of Max & Phyllis', 'Michelle of Neil & Michelle'. Falls back
// to the usual name, then the email, if there is no First Name.
export const formatSignedInAs = (preferredName, firstName, lastName, email, secondaryOf) => {
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const namePart = (preferredName && preferredName.trim()) || fullName;
  const of = typeof secondaryOf === 'string' ? secondaryOf.trim() : '';
  const first = typeof firstName === 'string' ? firstName.trim() : '';
  const own = of ? (first || namePart) : namePart;
  const self = own || email || '';
  return of && self ? `${self} of ${of}` : self;
};
